import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import axiosAuth from "../axiosAuth";
import { UPDATE_TASKASSIGNMENT, TASK_FILE_UPLOAD } from "./apiRoutes";
import { removeEmptyFields } from "@/lib/utils";

/**
 * -----------------------------
 * Types
 * -----------------------------
 */
export interface UpdateTaskAssignmentPayload {
  id: string; // used in URL
  taskName: string;
  priority: "LOW" | "MEDIUM" | "HIGH";
  taskStatus: string;
  taskAttachment?: string | File; // can be string (existing id) or File (new upload)
  taskAttachmentName?: string; // 👈 added to include file_name
  taskNotes: string;
  role: string;
  assignTo: string;
}

export interface UpdateTaskAssignmentResponse {
  status: number;
  error: boolean;
  message: string;
  data: any;
}

/**
 * -----------------------------
 * File Upload Utility
 * -----------------------------
 */
const uploadTaskFileAndGetInfo = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_type", "task_attachment");

  const res = await axiosAuth.post(TASK_FILE_UPLOAD, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const data = res?.data?.data;
  return {
    attachment_id: data?.attachment_id as string,
    file_name: data?.file_name as string,
  };
};

/**
 * -----------------------------
 * Mutation Hook (Final)
 * -----------------------------
 */
export const useUpdateTaskAssignmentMutation = (
  options?: UseMutationOptions<
    UpdateTaskAssignmentResponse,
    Error,
    UpdateTaskAssignmentPayload
  >
) => {
  return useMutation({
    mutationFn: async (payload: UpdateTaskAssignmentPayload) => {
      const { id, ...updateData } = payload;

      // ✅ Handle file upload if a File object is provided
      let taskAttachmentUuid = updateData.taskAttachment;
      let taskAttachmentName = updateData.taskAttachmentName;

      if (taskAttachmentUuid && typeof taskAttachmentUuid !== "string") {
        const fileInfo = await uploadTaskFileAndGetInfo(
          taskAttachmentUuid as File
        );
        taskAttachmentUuid = fileInfo.attachment_id;
        taskAttachmentName = fileInfo.file_name;
      }

      // Clean up empty/null fields
      const cleanedPayload = removeEmptyFields({
        ...updateData,
        taskAttachment: taskAttachmentUuid,
        taskAttachmentName, // 👈 added file_name
      });

      const response = await axiosAuth.put<UpdateTaskAssignmentResponse>(
        `${UPDATE_TASKASSIGNMENT}${id}`,
        cleanedPayload
      );

      return response.data;
    },
    ...options,
  });
};
